import React, { useState, useEffect } from "react";
import clsx from "clsx";
import { makeStyles } from "@material-ui/core/styles";
import MyDatePicker from "./DatePicker";
import { Paper, ClickAwayListener, Box } from "@mui/material";
import { CalendarSearch, X, ArrowRight } from "lucide-react";
import "./DateSelectionStyle.css";
import Button from "../Buttons/Button";

function DateRangePicker({ size = "small", resetDateRangeFilter, ...props }) {
  const classes = useStyles();
  var today = new Date();
  const [open, setOpen] = useState(false);
  const [custom, setCustom] = useState(true);
  const [weekSelected, setWeekSelected] = useState(false);
  const [monthSelected, setMonthSelected] = useState(true);
  const [yearSelected, setYearSelected] = useState(false);
  const [startDate, setStartDate] = useState(props.startDate);
  const [endDate, setEndDate] = useState(props.endDate);
  // Add temporary state for custom date selection
  const [tempStartDate, setTempStartDate] = useState(props.startDate);
  const [tempEndDate, setTempEndDate] = useState(props.endDate);

  const handleClickAway = () => {
    setOpen(false);
  };
  useEffect(() => {
    let diff = getNumberOfDays(props.startDate, props.endDate);
    if (props.startDate && sameDay(props.startDate, new Date())) {
      if (diff === 7) {
        setWeekSelected(true);
        setMonthSelected(false);
        setYearSelected(false);
        setCustom(false);
      } else if (diff === 30) {
        setWeekSelected(false);
        setMonthSelected(true);
        setYearSelected(false);
        setCustom(false);
      } else if (diff === 365) {
        setWeekSelected(false);
        setMonthSelected(false);
        setYearSelected(true);
        setCustom(false);
      } else {
        setWeekSelected(false);
        setMonthSelected(false);
        setYearSelected(false);
        setCustom(true);
      }
    } else {
      setWeekSelected(false);
      setMonthSelected(false);
      setYearSelected(false);
      setCustom(true);
    }
  }, [startDate, endDate]);

  function sameDay(d1, d2) {
    return d1.getFullYear() === d2.getFullYear() && d1.getMonth() === d2.getMonth() && d1.getDate() === d2.getDate();
  }

  function areDatesEqual(date1, date2) {
    if (!date1 || !date2) return false;
    return sameDay(new Date(date1), new Date(date2));
  }

  function areDateRangesDifferent() {
    return !areDatesEqual(tempStartDate, props.startDate) || !areDatesEqual(tempEndDate, props.endDate);
  }

  function getNumberOfDays(start, end) {
    const date1 = new Date(start);
    const date2 = new Date(end);

    // One day in milliseconds
    const oneDay = 1000 * 60 * 60 * 24;

    // Calculating the time difference between two dates
    const diffInTime = date2.getTime() - date1.getTime();

    // Calculating the no. of days between two dates
    const diffInDays = Math.round(diffInTime / oneDay);

    return diffInDays;
  }

  const formatDate = (date) => {
    const d = new Date(date);

    const day = String(d.getDate()).padStart(2, "0"); // Ensure 2 digits for day
    const month = String(d.getMonth() + 1).padStart(2, "0"); // Months are zero-based, add 1
    const year = d.getFullYear(); // Get the full year

    return `${day}/${month}/${year}`; // Return formatted string
  };
  const handleSetStartDate = (event) => {
    if (custom) {
      setTempStartDate(new Date(event));
    } else {
      if (props.index !== undefined && props.index !== null) {
        props.setDateRange(props.index, new Date(event), new Date(props.endDate));
        props.handleSetStartDate(props.index, event);
      } else {
        props.setDateRange(new Date(event), new Date(props.endDate));
        props.handleSetStartDate(event);
      }
    }
  };

  const handleSetEndDate = (event) => {
    if (custom) {
      setTempEndDate(new Date(event));
    } else {
      props.setDateRange(new Date(props.startDate), new Date(event));
      props.handleSetEndDate(event);
    }
  };

  const handleConfirmCustomDates = () => {
    if (tempStartDate && tempEndDate) {
      if (props.index !== undefined && props.index !== null) {
        props.setDateRange(props.index, tempStartDate, tempEndDate);
      } else {
        props.setDateRange(tempStartDate, tempEndDate);
      }
      setOpen(false);
    }
  };

  const handleOpen = () => {
    setOpen(!open);
  };

  const handleSeven = () => {
    let relevantDays = new Date(today);
    relevantDays.setDate(today.getDate() - 7);
    props.setDateRange(relevantDays, today);
    setWeekSelected(true);
    setYearSelected(false);
    setMonthSelected(false);
    setCustom(false);
  };

  const handleThirty = () => {
    let relevantDays = new Date(today);
    relevantDays.setDate(today.getDate() - 30);
    props.setDateRange(relevantDays, today);
    setMonthSelected(true);
    setYearSelected(false);
    setWeekSelected(false);
    setCustom(false);
  };

  const handleYear = () => {
    let relevantDays = new Date(today);
    relevantDays.setDate(today.getDate() - 365);
    props.setDateRange(relevantDays, today);
    setYearSelected(true);
    setMonthSelected(false);
    setWeekSelected(false);
    setCustom(false);
  };

  const handleCustom = () => {
    setCustom(!custom);
    setMonthSelected(false);
    setWeekSelected(false);
    setYearSelected(false);
    // Reset temporary dates when switching to custom mode
    setTempStartDate(props.startDate);
    setTempEndDate(props.endDate);
  };

  const clearDateRange = (e) => {
    e.stopPropagation();
    setOpen(false);
    resetSelected();
    resetDateRangeFilter();
  };

  const resetSelected = () => {
    setCustom(false);
    setMonthSelected(false);
    setWeekSelected(false);
    setYearSelected(false);
  };

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <div style={{ display: "inline-block" }}>
        <button
          onClick={handleOpen}
          className={classes.dateRangeButton}
          style={{
            height: size === "large" ? "40px" : "32px",
            width: "240px",
            // fontSize: size === "large" ? "16px" : "14px",
            paddingLeft: size === "large" ? "12px" : "8px",
            paddingRight: props.startDate && props.endDate ? "3px" : "8px",
          }}
        >
          {props.startDate && props.endDate ? (
            <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
              {formatDate(props.startDate)}
              <ArrowRight
                style={{
                  height: "12px",
                  width: "12px",
                }}
              />
              {formatDate(props.endDate)}
            </div>
          ) : (
            <span style={{ opacity: "0.6" }}>Select a date range</span>
          )}
          {props.startDate && props.endDate ? (
            <button
              onClick={(e) => clearDateRange(e)}
              className={classes.clearDateRangeButton}
              style={{
                width: size === "large" ? "32px" : "24px",
                height: size === "large" ? "32px" : "24px",
              }}
            >
              <X
                className={classes.buttonIcon}
                style={{
                  width: size === "large" ? "20px" : "16px",
                  height: size === "large" ? "20px" : "16px",
                }}
              />
            </button>
          ) : (
            <CalendarSearch
              className={classes.buttonIcon}
              style={{
                width: size === "large" ? "20px" : "16px",
                height: size === "large" ? "20px" : "16px",
              }}
            />
          )}
        </button>
        {open ? (
          // <Paper elevation={5} style={{ position: "absolute", zIndex: "999", marginTop: "6px" }} >
          <div className={classes.card}>
            <div className={classes.buttonContainer}>
              <button className={clsx(classes.toggleButton, { [classes.selected]: weekSelected })} onClick={handleSeven}>
                Previous week
              </button>
              <button className={clsx(classes.toggleButton, { [classes.selected]: monthSelected })} onClick={handleThirty}>
                Previous 30 days
              </button>
              <button className={clsx(classes.toggleButton, { [classes.selected]: yearSelected })} onClick={handleYear}>
                Previous year
              </button>
              <button className={clsx(classes.toggleButton, { [classes.selected]: custom })} onClick={handleCustom}>
                Date range
              </button>
            </div>
            <div className={classes.inputContainer}>
              {" "}
              <div className={classes.input}>
                <label className={classes.label}>From:</label>
                <MyDatePicker
                  style={{ marginRight: "1rem" }}
                  disabled={!custom}
                  value={custom ? tempStartDate : props.startDate}
                  toDate={custom ? tempEndDate : props.endDate}
                  action={handleSetStartDate}
                />
              </div>
              <div className={classes.input}>
                <label className={classes.label}>To:</label>
                <MyDatePicker value={custom ? tempEndDate : props.endDate} disabled={!custom} fromDate={custom ? tempStartDate : props.startDate} action={handleSetEndDate} />
              </div>
            </div>{" "}
            {custom && (
              <Button onClick={handleConfirmCustomDates} style={{ marginTop: "16px" }} fullWidth disabled={!tempStartDate || !tempEndDate || !areDateRangesDifferent()}>
                Apply Daterange
              </Button>
            )}
          </div>
        ) : (
          // </Paper>
          <React.Fragment></React.Fragment>
        )}
      </div>
    </ClickAwayListener>
  );
}
export default DateRangePicker;

const useStyles = makeStyles((theme) => ({
  card: {
    position: "absolute",
    zIndex: "999",

    backgroundColor: "white",

    border: "1px solid #EDEDED",
    borderRadius: "8px",

    boxShadow: "rgba(149, 157, 165, 0.2) 0px 8px 24px",

    marginTop: "6px",
    padding: "16px",

    [theme.breakpoints.down("xs")]: {
      left: "0px",
    },
  },

  buttonIcon: {
    width: "16px",
    height: "16px",
    display: "block",

    strokeWidth: "1.4px",
    color: "inherit",
  },

  dateRangeButton: {
    height: "28px",
    width: "240px",

    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",

    borderRadius: "4px",
    border: "1px solid #DCDCDC",

    backgroundColor: "white",

    fontSize: "14px",
    fontFamily: "Lato, sans-serif",
    color: "#0C0E29",

    "&:hover": {
      cursor: "pointer",
    },
  },

  buttonContainer: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: "8px",
    flexWrap: "wrap",

    marginBottom: "16px",
  },

  toggleButton: {
    height: "40px",
    padding: "0 12px",

    display: "flex",
    alignItems: "center",
    justifyContent: "center",

    position: "relative",

    border: "none",
    borderRadius: "4px",

    fontSize: "16px",
    color: "#a77d4f",
    backgroundColor: "transparent",
    border: "1px solid #a77d4f",

    transition: "all 0.2s ease-in-out",

    userSelect: "none",

    "&:hover": {
      backgroundColor: "#a77d4f",
      color: "white",
      cursor: "pointer",
    },
  },

  selected: {
    backgroundColor: "#a77d4f", // Style for the "selected" state
    color: "white",
    border: "1px solid #a77d4f", // Add a border for visual indication
  },

  clearDateRangeButton: {
    width: "24px",
    height: "24px",

    display: "flex",
    alignItems: "center",
    justifyContent: "center",

    borderRadius: "2px",
    border: "none",

    backgroundColor: "transparent",
    color: "#0C0E29",

    padding: "0",

    "&:hover": {
      cursor: "pointer",
      color: "#D84949",
      backgroundColor: "#EDEDED",
    },
  },

  inputContainer: {
    display: "flex",
    gap: "8px",
    flexDirection: "row",
    justifyContent: "space-evenly",

    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
    },
  },

  input: {
    display: "flex",
    flexDirection: "column",
    width: "50%",

    [theme.breakpoints.down("sm")]: {
      width: "100%",
    },
  },

  label: {
    fontSize: "16px",
    color: "rgb(105, 105, 105)",
    marginBottom: "4px",
  },
}));
